/**
 * Default layouts for editable marketing pages.
 *
 * DEFAULT_LAYOUTS['join-us-as-library'] is a faithful transcription of every
 * hardcoded string/value currently in the section files. It is used as the
 * client-side fallback when the API fetch fails or returns an unusable response.
 *
 * Rules:
 * - Every string here MUST match what the section renders today with no props.
 * - IDs for items (FAQ, steps, features, etc.) are deterministic slugs so that
 *   admin edits reference stable keys.
 * - Dynamic props (totalCount, libraries, loading) are NOT included here —
 *   JoinUsAsLibrary injects them at render time via dynamicPropsFor().
 */

export const DEFAULT_LAYOUTS = {
  'join-us-as-library': [
    {
      id: 'scrollProgressBar',
      type: 'scrollProgressBar',
      visible: true,
      props: {},
    },
    {
      id: 'marketingHeader',
      type: 'marketingHeader',
      visible: true,
      props: {
        ctaLabel: 'List your library',
        loginLabel: 'Login',
        showLogin: true,
      },
    },
    {
      id: 'hero',
      type: 'hero',
      visible: true,
      props: {
        eyebrow: 'For library owners',
        rotatingWords: ['Grow', 'Scale', 'Automate', 'Digitize'],
        headingPrefix: 'Fill every seat.',
        headingSuffix: 'your library.',
        body:
          'BookMyLibrary helps study libraries across India get discovered by students, fill seats with upfront digital bookings, accept payments seamlessly, and automate daily attendance — all from one mobile-first platform.',
        primaryCtaLabel: 'Start your trial',
        secondaryCtaLabel: 'Chat on WhatsApp',
        whatsappUrl:
          'https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library',
      },
    },
    {
      id: 'socialProof',
      type: 'socialProof',
      visible: true,
      props: {
        liveLabel: 'Live now',
        stats: [
          {
            id: 'libraries',
            value: 0,
            label: 'Study libraries listed',
            dynamic: 'libraries',
            suffix: '+',
          },
          {
            id: 'cities',
            value: 0,
            label: 'Cities across India',
            dynamic: 'cities',
            suffix: '+',
          },
          {
            id: 'seats',
            value: 400000,
            label: 'Study seats available',
            dynamic: null,
            suffix: '+',
          },
          {
            id: 'support',
            value: null,
            label: 'Support on WhatsApp',
            dynamic: null,
            suffix: null,
            staticDisplay: '24×7',
          },
        ],
      },
    },
    {
      id: 'pricing',
      type: 'pricing',
      visible: true,
      props: {
        eyebrow: 'Commercials',
        title: 'Free to list. Pay only when you earn.',
        subtitle: 'No upfront costs. No monthly fees. We only succeed when you do.',
        points: [
          'No setup fee — list your library for free, forever.',
          '5% commission only on successful online bookings — nothing on offline collections.',
          'Weekly payouts every Monday, straight to your bank account via NEFT.',
        ],
        ctaLabel: 'Start listing your library',
      },
    },
    {
      id: 'problems',
      type: 'problems',
      visible: true,
      props: {
        eyebrow: 'The challenges',
        title: "Running a library shouldn't be this hard.",
        items: [
          {
            id: 'empty-seats',
            icon: 'EventSeatOutlined',
            title: 'Empty seats going to waste',
            description: 'Vacant seats mean lost revenue every single day.',
          },
          {
            id: 'cash-headaches',
            icon: 'PaidOutlined',
            title: 'Cash collection headaches',
            description: 'Chasing fees and reconciling cash is time-consuming.',
          },
          {
            id: 'no-shows',
            icon: 'PersonOffOutlined',
            title: "No-shows you can't track",
            description:
              "Students book but don't show. You lose both the seat and the data.",
          },
          {
            id: 'manual-attendance',
            icon: 'MenuBookOutlined',
            title: 'Manual attendance register',
            description: 'Paper registers are slow, error-prone, and hard to audit.',
          },
        ],
      },
    },
    {
      id: 'benefits',
      type: 'benefits',
      visible: true,
      props: {
        eyebrow: 'How we help',
        title: 'Everything you need to grow.',
        items: [
          {
            id: 'always-full',
            icon: 'Groups',
            title: 'Always-full seats',
            description:
              'Your library appears in search when students look for study spots nearby. New students book and pay upfront.',
            flip: false,
          },
          {
            id: 'digital-payments',
            icon: 'CurrencyRupee',
            title: 'Instant digital payments',
            description:
              'Students pay via UPI, cards, and net banking through Razorpay. Money lands in your wallet. Withdraw anytime.',
            flip: true,
          },
          {
            id: 'qr-checkin',
            icon: 'QrCode2',
            title: 'Tap-to-scan check-in',
            description:
              "Every student gets a personal QR. You scan, they're checked in. No more paper register, no disputes.",
            flip: false,
          },
          {
            id: 'whatsapp-alerts',
            icon: 'WhatsApp',
            title: 'Automated WhatsApp alerts',
            description:
              'Booking confirmations, payment receipts, expiry reminders — sent on WhatsApp automatically.',
            flip: true,
          },
        ],
      },
    },
    {
      id: 'features',
      type: 'features',
      visible: true,
      props: {
        eyebrow: 'Everything you need',
        title: 'A complete platform for your library.',
        subtitle: 'Built for Indian study libraries — from seat booking to bank payouts.',
        items: [
          {
            id: 'seat-booking',
            icon: 'EventSeatOutlined',
            title: 'Seat booking',
            description:
              'Daily, weekly, monthly, and half-yearly plans. Students book and pay upfront.',
          },
          {
            id: 'qr-checkin',
            icon: 'QrCode2Outlined',
            title: 'QR check-in / out',
            description:
              'Every student gets a personal QR pass. Scan to mark attendance instantly.',
          },
          {
            id: 'wallet-payouts',
            icon: 'AccountBalanceWalletOutlined',
            title: 'Wallet & Razorpay payouts',
            description:
              'All payments collected digitally. Withdraw directly to your bank anytime.',
          },
          {
            id: 'analytics',
            icon: 'InsightsOutlined',
            title: 'Analytics dashboard',
            description:
              'Occupancy rates, revenue trends, and attendance reports at a glance.',
          },
          {
            id: 'whatsapp-otp',
            icon: 'WhatsApp',
            title: 'WhatsApp OTP & alerts',
            description:
              'OTP login, booking confirmations, and renewal reminders sent on WhatsApp.',
          },
          {
            id: 'mobile-portal',
            icon: 'SmartphoneOutlined',
            title: 'Mobile-first portal',
            description: 'Manage your library from your phone. No laptop required.',
          },
        ],
      },
    },
    {
      id: 'howItWorks',
      type: 'howItWorks',
      visible: true,
      props: {
        eyebrow: 'Simple onboarding',
        title: 'From signup to live in 48 hours.',
        subtitle: 'We handle the setup so you can focus on your students.',
        steps: [
          {
            id: 'step-1',
            number: '01',
            title: 'Tell us about your library',
            description: 'Fill the form below. Takes 2 minutes.',
          },
          {
            id: 'step-2',
            number: '02',
            title: 'We verify and onboard',
            description: 'Our team calls you, verifies details, sets up your library.',
          },
          {
            id: 'step-3',
            number: '03',
            title: 'Your library goes live',
            description: 'Seats, plans, and photos live. Students start booking immediately.',
          },
          {
            id: 'step-4',
            number: '04',
            title: 'You get paid',
            description: 'Funds settled to your wallet. Withdraw anytime.',
          },
        ],
      },
    },
    {
      id: 'priceTiers',
      type: 'priceTiers',
      visible: true,
      props: {
        eyebrow: 'Pricing',
        title: 'Choose your plan.',
        subtitle:
          'Everything your library needs to fill seats, collect payments, and grow — at a price that makes sense.',
        footnote:
          'All plans include onboarding support. No hidden fees. Cancel or upgrade anytime.',
        ctaLabel: 'Get started',
        tiers: [
          {
            id: 'tier-website',
            name: 'Library Website',
            price: '1999',
            billing_period: 'per year',
            badge: 'Most popular',
            highlight: true,
            features: [
              'Custom branded URL',
              'Choice of 5 themes',
              'Unlimited content updates',
              'Hosting & support included',
            ],
          },
          {
            id: 'tier-crm',
            name: 'CRM & Bookings',
            price: '999',
            billing_period: 'per year',
            badge: '',
            highlight: false,
            features: [
              'Seat booking & subscriptions',
              'QR check-in / check-out',
              'Digital wallet & payouts',
              'WhatsApp alerts included',
            ],
          },
          {
            id: 'tier-bundle',
            name: 'Full Bundle',
            price: '2999',
            billing_period: 'per year',
            badge: 'Best value',
            highlight: false,
            features: [
              'Everything in both plans',
              'Priority onboarding support',
              'Analytics dashboard',
              'Dedicated account manager',
            ],
          },
        ],
      },
    },
    {
      id: 'featuredLibraries',
      type: 'featuredLibraries',
      visible: true,
      props: {
        eyebrow: 'Real libraries. Real partners.',
        title: 'Libraries already thriving on BookMyLibrary.',
        subtitle:
          "Here's a glimpse of the libraries that trust us with their seats, their payments, and their students.",
      },
    },
    {
      id: 'faq',
      type: 'faq',
      visible: true,
      props: {
        eyebrow: 'Questions?',
        title: 'Frequently asked.',
        subtitle: "Can't find what you're looking for? Chat with us on WhatsApp.",
        items: [
          {
            id: 'faq-setup-time',
            question: 'How long does setup take?',
            answer: 'Usually 24–48 hours from the time you submit the form.',
          },
          {
            id: 'faq-payment',
            question: 'When do I get paid?',
            answer:
              'Payouts are processed every Monday via NEFT directly to the bank account linked to your librarian wallet. No holds, no surprises.',
          },
          {
            id: 'faq-setup-fee',
            question: 'Is there any setup fee?',
            answer:
              'No. Listing is completely free. We only earn when you do — via a small commission on successful online bookings. You keep 100% of your offline collections.',
          },
          {
            id: 'faq-existing-system',
            question: 'Can I use BookMyLibrary alongside my existing system?',
            answer: 'Yes. Most partners run both during the first month, then switch fully.',
          },
          {
            id: 'faq-multiple-branches',
            question: 'What if I have multiple branches?',
            answer:
              'Each branch is listed as its own library. You manage all of them from one dashboard.',
          },
          {
            id: 'faq-data',
            question: 'How is student data handled?',
            answer:
              'All data is stored securely in India. Only you and the student can access their records.',
          },
        ],
      },
    },
    {
      id: 'signup',
      type: 'signup',
      visible: true,
      props: {
        eyebrow: 'Get started',
        title: 'List your library with BookMyLibrary.',
        subtitle: "Fill the form. We'll call you within 24 hours.",
        pitchTitle: 'Join hundreds of libraries growing with BML.',
        pitchBody:
          'Our team will set everything up for you. You just need to fill the form and we\'ll take care of the rest.',
        whatsappCtaLabel: 'Chat with us on WhatsApp',
        whatsappUrl:
          'https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library',
        trustBullets: ['Free to list', '24-hour onboarding', 'No long-term contracts'],
      },
    },
    {
      id: 'marketingFooter',
      type: 'marketingFooter',
      visible: true,
      props: {
        tagline: "India's fastest-growing study library platform. Find a seat. Start studying.",
        productLinks: [
          { id: 'footer-features', label: 'Features', href: '#features' },
          { id: 'footer-how-it-works', label: 'How it works', href: '#how-it-works' },
          { id: 'footer-pricing', label: 'Pricing', href: '#pricing' },
          { id: 'footer-faq', label: 'FAQ', href: '#faq' },
        ],
        companyLinks: [
          { id: 'footer-about', label: 'About', href: '/#features' },
          { id: 'footer-contact', label: 'Contact', href: 'mailto:support@bookmylibrary.in' },
          { id: 'footer-careers', label: 'Careers', href: '#' },
          { id: 'footer-press', label: 'Press', href: '#' },
        ],
        legalLinks: [
          { id: 'footer-privacy', label: 'Privacy', href: '/privacy' },
          { id: 'footer-terms', label: 'Terms', href: '/terms' },
          { id: 'footer-refunds', label: 'Refunds', href: '/refunds' },
          {
            id: 'footer-attribution',
            label: 'Attribution',
            href: '/attribution',
            title: 'IP geolocation by DB-IP, licensed under CC BY 4.0',
          },
        ],
        contactEmail: 'support@bookmylibrary.in',
        phones: [
          { id: 'phone-in', label: '+91 85956 75155', href: 'tel:+918595675155' },
          { id: 'phone-us', label: '+1 555 985 5243', href: 'tel:+15559855243' },
        ],
        whatsappUrl: 'https://wa.me/918595675155',
        copyright: '© 2026 J2 Learning Pvt. Ltd. All rights reserved.',
      },
    },
  ],
};

export default DEFAULT_LAYOUTS;
